import { useRef, useState } from "react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Upload, Loader2 } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { isPersonaMoral } from "@/lib/clientLaboralProfile";
import {
  SOCIO_COUNT,
  emptyContactosPm,
  updatePersonInContactos,
  applyCsfFieldsToPerson,
  assessSocioAccionistaFromCsf,
  normalizeContactosPm,
} from "@/lib/clientContactosPm";

function PersonaFields({
  person,
  onField,
  onApplyPerson,
  role = "representante",
  csfHint = null,
}) {
  const fileRef = useRef(null);
  const [csfBusy, setCsfBusy] = useState(false);

  const onCsfSelected = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setCsfBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/clients/import-csf-pdf", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 120000,
      });
      const { parse_warnings, text_preview, ok, obligaciones_detectadas, ...fields } = data || {};
      const nextPerson = applyCsfFieldsToPerson(person, {
        ...fields,
        obligaciones_detectadas,
      });
      const hint =
        role === "socio"
          ? assessSocioAccionistaFromCsf({
              ...fields,
              obligaciones_detectadas,
            })
          : null;
      onApplyPerson(nextPerson, hint);
      const bits = [];
      if (nextPerson.rfc) bits.push("RFC");
      if (nextPerson.nombre) bits.push("nombre");
      if (nextPerson.curp) bits.push("CURP");
      toast.success(
        bits.length
          ? `Constancia leída: ${bits.join(", ")}. Revise los datos.`
          : "Constancia leída. Revise o complete los datos.",
      );
      if (Array.isArray(parse_warnings) && parse_warnings.length) {
        toast.message(parse_warnings[0]);
      }
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "No se pudo leer el PDF");
    } finally {
      setCsfBusy(false);
    }
  };

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="rounded-sm h-8 text-xs"
          disabled={csfBusy}
          onClick={() => fileRef.current?.click()}
          data-testid={`contacto-pm-csf-${role}`}
        >
          {csfBusy ? (
            <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
          ) : (
            <Upload className="h-3.5 w-3.5 mr-1.5" />
          )}
          {csfBusy ? "Leyendo CSF…" : "Subir constancia (PDF)"}
        </Button>
        <input
          ref={fileRef}
          type="file"
          accept="application/pdf,.pdf"
          className="hidden"
          onChange={onCsfSelected}
        />
        <span className="text-[10px] text-muted-foreground">
          Copia RFC, nombre y CURP de la CSF de esta persona (no cambia datos de la empresa).
        </span>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label className="text-xs">RFC</Label>
          <Input
            value={person.rfc || ""}
            onChange={(e) => onField("rfc", e.target.value.toUpperCase())}
            className="rounded-sm font-mono mt-1"
            maxLength={13}
          />
        </div>
        <div>
          <Label className="text-xs">CURP</Label>
          <Input
            value={person.curp || ""}
            onChange={(e) =>
              onField(
                "curp",
                e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, "").slice(0, 18),
              )
            }
            className="rounded-sm font-mono mt-1"
            maxLength={18}
            placeholder="18 caracteres"
            data-testid={`contacto-pm-curp-${role}`}
          />
        </div>
        <div className="col-span-2">
          <Label className="text-xs">Nombre</Label>
          <Input
            value={person.nombre || ""}
            onChange={(e) => onField("nombre", e.target.value)}
            className="rounded-sm mt-1"
          />
        </div>
        <div>
          <Label className="text-xs">WhatsApp</Label>
          <Input
            value={person.whatsapp || ""}
            onChange={(e) => onField("whatsapp", e.target.value)}
            className="rounded-sm font-mono mt-1"
          />
        </div>
        <div className="flex items-end pb-2">
          <label className="flex items-center gap-2 text-xs text-slate-700">
            <input
              type="checkbox"
              checked={Boolean(person.acepta_whatsapp)}
              onChange={(e) => onField("acepta_whatsapp", e.target.checked)}
            />
            Acepta WhatsApp
          </label>
        </div>
      </div>

      {role === "socio" && csfHint ? (
        <div
          className={`rounded-sm border px-3 py-2 text-[11px] space-y-1 ${
            csfHint.matched
              ? "border-emerald-200 bg-emerald-50 text-emerald-900"
              : "border-amber-200 bg-amber-50 text-amber-950"
          }`}
          data-testid="contacto-pm-socio-hint"
        >
          <p className="m-0 font-medium">
            {csfHint.matched ? "Socio / accionista detectado en CSF" : "Revisar Socio / accionista"}
          </p>
          <p className="m-0">{csfHint.message}</p>
          {csfHint.regimen ? (
            <p className="m-0 text-[10px] opacity-90">Régimen leído: {csfHint.regimen}</p>
          ) : null}
          {csfHint.obligaciones?.length ? (
            <details className="mt-1">
              <summary className="cursor-pointer text-[10px] font-medium">
                Obligaciones leídas del PDF ({csfHint.obligaciones.length})
              </summary>
              <ul className="m-0 mt-1 pl-4 list-disc space-y-0.5">
                {csfHint.obligaciones.map((ob, i) => (
                  <li key={i}>{ob}</li>
                ))}
              </ul>
            </details>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

export default function ClientContactosSection({ form, setForm }) {
  const isPM = isPersonaMoral(form);
  const contactos = form.contactos_pm || emptyContactosPm();
  const [csfHints, setCsfHints] = useState({});

  const patchPerson = (section, index, field, value) => {
    setForm((prev) => updatePersonInContactos(prev, section, index, field, value));
  };

  const replacePerson = (section, index, nextPerson, hint) => {
    setForm((prev) => {
      const cm = normalizeContactosPm(prev.contactos_pm || emptyContactosPm());
      if (section === "representante_legal") {
        cm.representante_legal = { ...cm.representante_legal, ...nextPerson };
      } else if (section === "socios" && index >= 0 && index < SOCIO_COUNT) {
        cm.socios[index] = { ...cm.socios[index], ...nextPerson };
      }
      return { ...prev, contactos_pm: cm };
    });
    const key = section === "representante_legal" ? "representante" : `socio-${index}`;
    setCsfHints((prev) => ({ ...prev, [key]: hint }));
  };

  return (
    <div className="col-span-2 rounded-md border border-slate-200 bg-slate-50/80 p-3 space-y-3">
      <p className="text-xs font-medium text-[#0F2D5C] m-0">Contacto y comunicación</p>
      <p className="text-[11px] text-muted-foreground m-0">
        Datos compartidos con Cliente Maestro del despacho. No activan SAT ni descargas automáticas.
      </p>
      <div className="grid grid-cols-2 gap-3">
        <div className="col-span-2">
          <Label className="text-xs">Nombre comercial</Label>
          <Input
            value={form.nombre_comercial}
            onChange={(e) => setForm({ ...form, nombre_comercial: e.target.value })}
            className="rounded-sm mt-1"
            placeholder={form.razon_social ? `Por defecto: ${form.razon_social}` : "Se rellena con la razón social; puede corregirlo"}
            data-testid="client-nombre-comercial-input"
          />
          <p className="text-[10px] text-muted-foreground m-0 mt-1">
            Se propone automáticamente desde la razón social del cliente; puede editarlo si usa otra marca.
          </p>
        </div>
        <div>
          <Label className="text-xs">Correo</Label>
          <Input
            type="email"
            value={form.email}
            onChange={(e) => setForm({ ...form, email: e.target.value })}
            className="rounded-sm mt-1"
          />
        </div>
        <div>
          <Label className="text-xs">Correo alterno</Label>
          <Input
            type="email"
            value={form.email_alterno}
            onChange={(e) => setForm({ ...form, email_alterno: e.target.value })}
            className="rounded-sm mt-1"
          />
        </div>
        <div>
          <Label className="text-xs">WhatsApp principal</Label>
          <Input
            value={form.whatsapp_principal}
            onChange={(e) => setForm({ ...form, whatsapp_principal: e.target.value })}
            className="rounded-sm font-mono mt-1"
          />
        </div>
        <div>
          <Label className="text-xs">WhatsApp alterno</Label>
          <Input
            value={form.whatsapp_alterno}
            onChange={(e) => setForm({ ...form, whatsapp_alterno: e.target.value })}
            className="rounded-sm font-mono mt-1"
          />
        </div>

        {isPM ? (
          <div className="col-span-2 pt-1">
            <Accordion type="multiple" defaultValue={["representante"]} className="rounded-md border bg-white px-3">
              <AccordionItem value="representante" className="border-b-0">
                <AccordionTrigger className="text-xs font-medium py-2">
                  Representante legal
                </AccordionTrigger>
                <AccordionContent className="pb-3">
                  <PersonaFields
                    person={contactos.representante_legal}
                    role="representante"
                    csfHint={csfHints.representante}
                    onField={(field, value) => patchPerson("representante_legal", -1, field, value)}
                    onApplyPerson={(next, hint) => replacePerson("representante_legal", -1, next, hint)}
                  />
                </AccordionContent>
              </AccordionItem>
              {Array.from({ length: SOCIO_COUNT }, (_, i) => (
                <AccordionItem key={i} value={`socio-${i}`} className="border-b-0 border-t">
                  <AccordionTrigger className="text-xs font-medium py-2">
                    Socio {i + 1} (opcional)
                  </AccordionTrigger>
                  <AccordionContent className="pb-3">
                    <PersonaFields
                      person={contactos.socios[i]}
                      role="socio"
                      csfHint={csfHints[`socio-${i}`]}
                      onField={(field, value) => patchPerson("socios", i, field, value)}
                      onApplyPerson={(next, hint) => replacePerson("socios", i, next, hint)}
                    />
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        ) : (
          <>
            <div className="col-span-2">
              <p className="text-[11px] text-muted-foreground m-0 mb-2">
                Persona física: el titular del RFC se considera representante legal al guardar.
              </p>
            </div>
            <div className="col-span-2" data-testid="client-curp-contacto-field">
              <Label className="text-xs">CURP</Label>
              <Input
                data-testid="client-curp-contacto-input"
                value={form.curp || ""}
                onChange={(e) =>
                  setForm({
                    ...form,
                    curp: e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, "").slice(0, 18),
                    tipo_persona: "fisica",
                  })
                }
                className="rounded-sm font-mono mt-1"
                placeholder="18 caracteres"
                maxLength={18}
                autoComplete="off"
              />
            </div>
          </>
        )}

        <div>
          <Label className="text-xs">Canal preferido</Label>
          <Input
            value={form.canal_preferido}
            onChange={(e) => setForm({ ...form, canal_preferido: e.target.value })}
            className="rounded-sm mt-1"
            placeholder="email, whatsapp, teléfono…"
          />
        </div>
        <div className="col-span-2 flex flex-wrap gap-4 pt-1">
          <label className="flex items-center gap-2 text-xs text-slate-700">
            <input
              type="checkbox"
              checked={Boolean(form.acepta_recordatorios)}
              onChange={(e) => setForm({ ...form, acepta_recordatorios: e.target.checked })}
            />
            Acepta recordatorios
          </label>
          {!isPM ? (
            <label className="flex items-center gap-2 text-xs text-slate-700">
              <input
                type="checkbox"
                checked={Boolean(form.acepta_whatsapp)}
                onChange={(e) => setForm({ ...form, acepta_whatsapp: e.target.checked })}
              />
              Acepta WhatsApp
            </label>
          ) : null}
        </div>
      </div>
    </div>
  );
}
